import { useState } from "react";
import BaseFormModal from "@/components/forms/BaseFormModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormBase } from "@/hooks/useFormBase";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { pushToDataLayer } from "@/utils/dataLayerUtils";
import { useAmplitudeEvents } from "@/utils/amplitudeEvents";
import { getPropertyShortName } from "@/utils/propertyUtils";
import { formatFullNumericDate } from "@/utils/shared/dateUtils";
import { bookingService } from "@/services";
import { z } from "zod";

const bookingRequestSchema = z.object({
  full_name: z.string().min(2, "Nombre es requerido"),
  email: z.string().email("Email inválido"),
  phone: z.string().min(8, "Teléfono es requerido"),
});

type BookingFormData = z.infer<typeof bookingRequestSchema>;

interface BookingModalProps {
  isOpen: boolean;
  onSubmit: () => void;
  closeModal: () => void;
  propertyTitle: string;
  checkIn?: Date;
  checkOut?: Date;
  nights: number;
  totalPrice: number;
  guests?: string;
  property?: {
    rating: number;
    guests: number;
    reviews?: any[];
    images?: string[];
    host?: { name: string };
  };
  maxGuests?: number;
  price?: number;
}

const BookingModal = ({
  isOpen,
  onSubmit,
  closeModal,
  propertyTitle,
  checkIn,
  checkOut,
  nights,
  totalPrice,
  guests = "1",
  property,
  maxGuests,
  price,
}: BookingModalProps) => {
  const isMobile = useIsMobile();
  const { trackBookingRequested, trackBookingModalClosed } = useAmplitudeEvents();

  const form = useFormBase<BookingFormData>({
    schema: bookingRequestSchema,
    successMessage: "¡Solicitud enviada! Nos pondremos en contacto contigo pronto.",
    errorMessage: "Error al enviar la solicitud. Por favor intenta nuevamente.",
  });

  const handleClose = () => {
    trackBookingModalClosed(
      checkIn || new Date(),
      checkOut || new Date(),
      guests,
      propertyTitle,
      getPropertyShortName(propertyTitle),
      property?.rating || 0,
      property?.guests || maxGuests || 0,
      price || 0,
      property?.reviews?.length || 0,
      property?.images?.length || 0,
      property?.host?.name || ""
    );
    closeModal();
  };

  const handleSubmitBooking = async (data: BookingFormData) => {
    if (!checkIn || !checkOut) {
      console.error('[BookingModal] Missing dates:', { checkIn, checkOut });
      return;
    }

    try {
      // Create booking request using service
      const bookingData = {
        property_title: propertyTitle,
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        check_in: checkIn.toISOString().split('T')[0],
        check_out: checkOut.toISOString().split('T')[0],
        nights,
        total_price: totalPrice,
        status: 'pending' as const,
      };

      console.log('[BookingModal] Sending booking request:', bookingData);

      const result = await bookingService.createBookingRequest(bookingData);
      
      console.log('[BookingModal] Booking request result:', result);
      
      if (result.success) {
        // Track analytics
        trackBookingRequested(
          checkIn,
          checkOut,
          guests,
          propertyTitle,
          getPropertyShortName(propertyTitle),
          property?.rating || 0,
          property?.guests || maxGuests || 0,
          price || 0,
          property?.reviews?.length || 0,
          property?.images?.length || 0,
          property?.host?.name || ""
        );

        // Push to data layer
        pushToDataLayer({
          event: "booking_request_submitted",
          property_title: propertyTitle,
          check_in: formatFullNumericDate(checkIn),
          check_out: formatFullNumericDate(checkOut),
          nights,
          guests: parseInt(guests),
          total_price: totalPrice,
        });

        onSubmit();
      } else {
        console.error('[BookingModal] Booking request failed:', result.error);
        throw new Error(result.error || 'Failed to create booking request');
      }
    } catch (error) {
      console.error('[BookingModal] Error creating booking request:', error);
      throw error;
    }
  };

  const formatPrice = (amount: number) => `$${amount.toLocaleString()} MXN`;
  const formatGuests = (guestCount: string) => {
    const count = parseInt(guestCount);
    return count === 1 ? "1 huésped" : `${count} huéspedes`;
  };

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Solicitud de Reservación"
      onSubmit={form.handleSubmit(handleSubmitBooking)}
      submitText="Enviar solicitud"
      isSubmitting={form.isSubmitting}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="full_name">Nombre completo</Label>
          <Input
            id="full_name"
            {...form.register("full_name")}
            placeholder="Tu nombre completo"
            className="mt-1"
          />
          {form.getFieldError("full_name") && (
            <p className="text-sm text-destructive mt-1">
              {form.getFieldError("full_name")}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Correo electrónico</Label>
          <Input
            id="email"
            type="email"
            {...form.register("email")}
            placeholder="tu@correo.com"
            className="mt-1"
          />
          {form.getFieldError("email") && (
            <p className="text-sm text-destructive mt-1">
              {form.getFieldError("email")}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="phone">Teléfono</Label>
          <Input
            id="phone"
            {...form.register("phone")}
            placeholder="Tu número de teléfono"
            className="mt-1"
          />
          {form.getFieldError("phone") && (
            <p className="text-sm text-destructive mt-1">
              {form.getFieldError("phone")}
            </p>
          )}
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-2">
          <h4 className="font-semibold text-foreground">
            Resumen de la reservación
          </h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <span className="font-medium">Propiedad:</span> {propertyTitle}
            </p>
            {checkIn && checkOut && (
              <>
                <p>
                  <span className="font-medium">Fechas:</span>{" "}
                  {formatFullNumericDate(checkIn)} - {formatFullNumericDate(checkOut)}
                </p>
                <p>
                  <span className="font-medium">Huéspedes:</span> {formatGuests(guests)}
                </p>
                <p>
                  <span className="font-medium">Noches:</span> {nights}
                </p>
                <p>
                  <span className="font-medium">Total:</span> {formatPrice(totalPrice)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </BaseFormModal>
  );
};

export default BookingModal;